import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ChevronsUpDown, LogOut, MapPin, Check } from "lucide-react";
import { MobileMenuButton } from "./Sidebar";

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function Header({ onToggleMobile }) {
  const { session, logout } = useAuth();
  const { activeTenant, authorizedBranches: branches, activeBranch, selectBranch, isSuperAdmin } = useTenant();
  const navigate = useNavigate();

  const user = session?.user;

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-20 h-16 bg-white/95 backdrop-blur border-b border-border flex items-center gap-3 px-4 md:px-8"
    >
      <MobileMenuButton onClick={onToggleMobile} />

      {/* Tenant identity */}
      <div className="flex items-center gap-3 min-w-0">
        {activeTenant?.logo_url ? (
          <img
            src={
              activeTenant.logo_url.startsWith("http")
                ? activeTenant.logo_url
                : `${process.env.REACT_APP_BACKEND_URL}${activeTenant.logo_url}`
            }
            alt={activeTenant.commercial_name}
            className="h-8 w-8 object-cover rounded border border-border"
            data-testid="header-tenant-logo"
          />
        ) : (
          <div className="h-8 w-8 rounded bg-tenant text-white text-xs flex items-center justify-center font-semibold">
            {activeTenant ? initials(activeTenant.commercial_name) : "VR"}
          </div>
        )}
        <div className="min-w-0 hidden sm:block">
          <div
            className="text-sm font-semibold truncate max-w-[260px]"
            data-testid="header-tenant-name"
          >
            {activeTenant?.commercial_name || (isSuperAdmin ? "VRIntegrales · Global" : "TallerOS")}
          </div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">
            {isSuperAdmin ? "Vista Super Admin" : activeTenant?.plan_name || "TallerOS"}
          </div>
        </div>
      </div>

      {/* Branch selector */}
      {branches.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="ml-2 md:ml-6 h-9 gap-2 max-w-[220px]"
              data-testid="header-branch-selector"
            >
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="truncate text-sm">
                {activeBranch?.name || "Sucursal"}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Sucursal activa</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {branches.map((b) => (
              <DropdownMenuItem
                key={b.id}
                data-testid={`branch-option-${b.id}`}
                onClick={() => selectBranch(b.id)}
                className="cursor-pointer"
              >
                <div className="flex-1">
                  <div className="text-sm">{b.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {b.is_main ? "Principal" : b.code || b.branch_type}
                  </div>
                </div>
                {activeBranch?.id === b.id && <Check className="h-4 w-4 text-tenant" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <div className="flex-1" />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-3 pl-2 pr-1 py-1 rounded-md hover:bg-slate-50 transition-colors"
            data-testid="header-user-menu"
          >
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight">{user?.full_name}</div>
              <div className="text-[11px] text-muted-foreground">
                {ROLE_LABELS[user?.role_name] || user?.role_name}
              </div>
            </div>
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-tenant text-white text-xs">
                {initials(user?.full_name)}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-sm font-medium">{user?.full_name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={doLogout} data-testid="header-logout">
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
